import calendar from '@iconify/icons-carbon/calendar';
import directionStraightRight from '@iconify/icons-carbon/direction-straight-right';
import { Box, Button, Container, Grid, List, ListItem, Typography } from '@mui/material';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';

import { Consumer, Page, Provider, Section } from '@larvitu/shared/mui/ui-components';
import { Iconify } from '@larvitu/shared/mui/ui-iconify';
import { Link } from '@larvitu/shared/next/mui';
import { For } from '@larvitu/shared/react/components/feature-for';

import stockDoc3 from '../../public/assets/images/geiges/801_Götz_Geiges_037.jpg';
import { ContactUs, FAQS, QuickHelpComponent, SectionStyle } from '../components';
import { getLayout } from '../lib/getLayout';
import { getServerTranslations } from '../lib/i18n/getServerTranslations';

import type { AppPage } from '../@types/page';
import type { forEach } from '@larvitu/shared/react/components/feature-for';
import type { GetStaticProps, InferGetStaticPropsType } from 'next';
import type { FC } from 'react';

interface ListItemComponentProps {
  bulletPoint?: string;
  content?: string;
  subList?: string;
  items?: [string];
}

const ListItemComponent: FC<forEach.Iterable<ListItemComponentProps>> = ({
  data,
}) => (
  <ListItem
    alignItems="flex-start"
    sx={{
      paddingLeft: 0,
      flexDirection: { xs: 'column', sm: 'row' },
      '&:before': {
        minWidth: '125px',
        content: `${data?.bulletPoint ? data.bulletPoint.replace(/<br\s*\/?>/gi, '\\A') : '\'\''}`,
        whiteSpace: 'pre-wrap',
      },
    }}
  >
    <Typography
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
      }}
    >
      {data?.content ?? null}
      {data?.items?.map(
        (item, index) =>
          (
            <Typography component="span" key={index}>
              {item}
            </Typography>
          )
      )}
    </Typography>
  </ListItem>
);

// -----------------------------------------------------------------------------
// *****************************************************************************
// -----------------------------------------------------------------------------

// interface WorkItems
interface WorkItems {
  id: string;
  bulletPoint: string;
  content: string;
}

// interface Membership
interface Membership {
  title: string;
  content: string[];
}

// interface StaffCard
interface StaffCardProps {
  imgPos?: string;
  name?: string;
  graduation?: string;
  country?: string;
  content?: string;
  membership?: Membership;
  subContent?: string;
  order?: number;
  picture?: string;
  items?: WorkItems[];
}

// creation staffcard
const StaffCard: FC<forEach.Iterable<StaffCardProps>> = ({ data }) => {
  const { t } = useTranslation( ['aboutUs', 'common', 'faq', 'contact'] );
  const containedButtonHover = {
    backgroundColor: 'rgba(24, 84, 77, 0.1)',
    border: '1px solid #174741',
    color: 'rgba(24, 84, 77, 1)',
  };

  return (
    <SectionStyle>
      <Container>
        <Grid
          container
          rowSpacing={{
            xs: 5,
            lg: 0,
          }}
          columnSpacing={{ lg: 3 }}
        >
          <Grid
            item
            xs={12}
            lg={6}
            order={{ lg: data?.order }}
            display="flex"
            justifyContent="center"
          >
            <Box
              position="relative"
              width={{ xs: '100%', sm: 450, md: 600, lg: '100%' }}
              height={{ xs: 300, sm: 450, md: 600, lg: '100%' }}
              maxHeight={650}
            >
              <Image
                sizes="(max-width: 600) 100vw,(max-width: 900px) 80vw, (max-width: 1200px) 70vw, (max-width: 1536px) 65vw, 900px"
                src={data?.picture ?? ''}
                alt={data?.name ?? ''}
                style={{
                  objectFit: 'cover',
                  objectPosition: data?.imgPos,
                }}
                fill
                priority
              />
            </Box>
          </Grid>
          <Grid item xs={12} lg={6}>
            <Box>
              <Typography fontWeight="bold" mb={2} gutterBottom variant="h2">
                {data?.name}
              </Typography>
              <Typography color="text.secondary" mb={4} gutterBottom paragraph>
                {data?.graduation}{ data?.graduation && data?.country && ', '}{data?.country}
              </Typography>
              <List>
                <For each={data?.items}>
                  <ListItemComponent />
                </For>
              </List>
            </Box>
          </Grid>

          <Grid item xs={12} display="flex" flexDirection="column" order={3}>
            <Typography my={3} component="h3" variant="h4" fontWeight="bold">
              {data?.membership?.title}:
            </Typography>
            <Grid container spacing={1}>
              {data?.membership?.content.map((item, index) => (
                <Grid
                  item
                  key={index}
                  xs={12}
                  sm={6}
                  display="flex"
                  alignItems="center"
                  gap={2}
                >
                  <Box minWidth={16} display="flex">
                    <Iconify icon={directionStraightRight} />
                  </Box>
                  <Typography>
                    {item}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </Grid>

          <Grid item xs={12} sm={6} order={4} mt={3}>
            <Link href="contact">
              <Button
                size="large"
                sx={{ minWidth: 240, '&:hover': containedButtonHover }}
                variant="contained"
                endIcon={<Iconify icon={calendar} />}
              >
                {t( 'to-the-contact-form', { ns: 'common' } )}
              </Button>
            </Link>
          </Grid>
        </Grid>
      </Container>
    </SectionStyle>
  );
};

// -----------------------------------------------------------------------------
// *****************************************************************************
// -----------------------------------------------------------------------------

const AboutUs: AppPage<InferGetStaticPropsType<typeof getStaticProps>> = () => {
  const { t } = useTranslation( ['aboutUs', 'faq', 'contact-us'] );

  return (
    <Page title="Über uns">
      {/* ----------------------------------------------------------------------------- */}

      <Section id="hero">
        <Box
          id="serviceGradiantPicture"
          position="absolute"
          width="100%"
          height={350}
          zIndex={0}
        >
          {/*<Image*/}
          {/*  // layout="fill"*/}
          {/*  fill*/}
          {/*  src="/assets/images/AdobeStock_286053032.jpeg"*/}
          {/*  // objectFit="cover"*/}
          {/*  style={{*/}
          {/*    objectFit: 'cover',*/}
          {/*    background: 'linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #FFFFFF 100%) ',*/}
          {/*    backgroundSize: 'cover',*/}
          {/*    backgroundPosition: 'center',*/}
          {/*    // display: block !important;*/}
          {/*  }}*/}
          {/*/>*/}
          <Box className="bgPicture" height={{ xs: 200, md: 350 }}>
          </Box>
        </Box>
        <SectionStyle>
          <Container
            sx={{
              position: 'relative',
              zIndex: 1,
              width: '100%',
              height: '100%',
              alignSelf: 'center',
              marginTop: { xs: 15, md: 25, lg: 30 },
            }}
          >
            <Box maxWidth={{ lg: '90%' }}>
              <Typography variant="h1">
                {t( 'story.header', { ns: 'aboutUs' } )}
              </Typography>
            </Box>
          </Container>
        </SectionStyle>
      </Section>

      {/* ----------------------------------------------------------------------------- */}

      <Section id="doctors">
        <For
          each={t( 'team.groups.doctors', {
            returnObjects: true,
            defaultValue: '',
          } )}
        >
          <Provider>
            <Consumer>
              <StaffCard />
            </Consumer>
          </Provider>
        </For>
      </Section>

      {/* ----------------------------------------------------------------------------- */}

      <Section id="contactUs">
        <ContactUs
          header={t( 'arrival', { ns: 'contact' } )}
          openingHours={t( 'opening-hours', {
            ns: 'contact',
            returnObjects: true,
          } )}
          address={t( 'address', { ns: 'contact', returnObjects: true } )}
          content={t( 'content', { ns: 'contact', returnObjects: true } )}
        />

        <FAQS
          items={t( 'items', {
            ns: 'faq',
            returnObjects: true,
          } )}
        />
      </Section>

      {/* ----------------------------------------------------------------------------- */}

      <Section id="extra-info">
        <QuickHelpComponent
          title={t( 'extra-info.header', { ns: 'aboutUs' } )}
          // image={stockDoc3}
          containedButton={t( 'toService', { ns: 'common' } )}
          containedLink="/services"
          callButton={false}
        />
      </Section>

      {/* ----------------------------------------------------------------------------- */}
    </Page>
  );
};

AboutUs.getLayout = getLayout;

export const getStaticProps: GetStaticProps = async ({ locale = '' }) => ({
  props: {
    ...(await getServerTranslations( locale, [
      'common',
      'aboutUs',
      'faq',
      'contact',
    ] )),
  },
});

export default AboutUs;
